import { basketSections } from "../Session/plan-lesson/ai-lesson-planning/recommendationsApi"
import type {
  AiSubject,
  RecommendationApiBasketItem,
  RecommendationApiData,
} from "../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"
import type { LearnerAttempt, LearnerReportData } from "./learnerReportData"

export const SUBJECT_ORDER: AiSubject[] = ["math", "english"]

export const SUBJECT_META: Record<
  AiSubject,
  { label: string; bg: string; fg: string; solid: string }
> = {
  math: { label: "Maths", bg: "#E4F4EA", fg: "#1F7A45", solid: "#1F7A45" },
  english: { label: "English", bg: "#F2E7F8", fg: "#7A3B99", solid: "#7A3B99" },
}

const STRAND_COLORS = [
  "#1F9E6E", "#3B5BCC", "#6A4BC4", "#B0294A", "#C77700", "#7A3B99", "#2E8FA8",
]

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const DAY_MS = 86400000

export type SkillBand = "Not started" | "Passed" | "Good" | "In progress" | "Weak"

export interface BandStyle {
  label: SkillBand
  solid: string
  bg: string
  fg: string
}

/** Skill scores are on a 0–10 scale. */
export function skillBand(score: number | null): BandStyle {
  if (score === null) return { label: "Not started", solid: "#C3C8D4", bg: "#EEF0F5", fg: "#4A5163" }
  if (score >= 9) return { label: "Passed", solid: "#2E9D5E", bg: "#E6F5EC", fg: "#1E6E40" }
  if (score >= 7) return { label: "Good", solid: "#3B5BCC", bg: "#E4EAFB", fg: "#2E4AA6" }
  if (score >= 4) return { label: "In progress", solid: "#E0A040", bg: "#FFF2DE", fg: "#8A5300" }
  return { label: "Weak", solid: "#D7354B", bg: "#FDE6E9", fg: "#A8243A" }
}

/** Activity scores are on a 0–100 scale. */
export function activityBand(score: number | null): { label: string; bg: string; fg: string } {
  if (score === null) return { label: "Not scored", bg: "#EEF0F5", fg: "#4A5163" }
  if (score >= 90) return { label: "Passed", bg: "#E6F5EC", fg: "#1E6E40" }
  if (score >= 70) return { label: "Good", bg: "#E4EAFB", fg: "#2E4AA6" }
  if (score >= 40) return { label: "In progress", bg: "#FFF2DE", fg: "#8A5300" }
  return { label: "Weak", bg: "#FDE6E9", fg: "#A8243A" }
}

export function activityScoreColor(score: number | null): string {
  if (score === null) return "#4A5163"
  if (score >= 90) return "#1E6E40"
  if (score >= 70) return "#2E4AA6"
  if (score >= 40) return "#8A5300"
  return "#B42335"
}

export function heatCellColors(score: number | null): [string, string] {
  if (score === null) return ["#F3F4F8", "#8C93A3"]
  if (score >= 9) return ["#C7EBD4", "#165A33"]
  if (score >= 7) return ["#CDD7F6", "#22398C"]
  if (score >= 4) return ["#FBE2B8", "#6E4200"]
  return ["#F6C9CF", "#8A1C2C"]
}

export function shortDate(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

export function longDate(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export function clockTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

export function updatedStamp(value?: string | null): string {
  if (!value) return "—"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "—"
  return `${shortDate(d)} ${clockTime(d)}`
}

function keyOf(name: string): string {
  return name.trim().toLowerCase()
}

function avg(values: number[]): number | null {
  if (values.length === 0) return null
  return values.reduce((t, v) => t + v, 0) / values.length
}

// ---------------------------------------------------------------------------
// Activities (tries grouped by content)
// ---------------------------------------------------------------------------

export interface TryEntry {
  at: Date
  score: number | null
}

export type RetryVerdict = "Learning" | "Slow gain" | "Stalled" | "Guessing"

export interface ActivityGroup {
  key: string
  subject: AiSubject
  sourceId: string
  title: string
  typeLabel: string
  skill: string
  strand: string
  year: number | null
  tries: TryEntry[]
  best: number | null
  last: Date
  first: Date
  gainPerTry: number | null
  verdict: RetryVerdict | null
  note: string
}

function verdictFor(tries: TryEntry[]): { gain: number | null; verdict: RetryVerdict | null } {
  const scored = tries.filter(t => t.score !== null) as Array<{ at: Date; score: number }>
  if (scored.length < 2) return { gain: null, verdict: null }
  const gain = (scored[scored.length - 1].score - scored[0].score) / (scored.length - 1)
  const spanMin = (scored[scored.length - 1].at.getTime() - scored[0].at.getTime()) / 60000
  if (scored.length >= 3 && spanMin <= 5 && gain < 2) return { gain, verdict: "Guessing" }
  if (gain >= 10) return { gain, verdict: "Learning" }
  if (gain >= 5) return { gain, verdict: "Slow gain" }
  return { gain, verdict: "Stalled" }
}

function noteFor(group: Omit<ActivityGroup, "note">, nextTitle: string | null): string {
  const scores = group.tries.map(t => t.score).filter((s): s is number => s !== null)
  const rising = scores.every((s, i) => i === 0 || s >= scores[i - 1])
  switch (group.verdict) {
    case "Learning": {
      const spanMin = Math.round((group.last.getTime() - group.first.getTime()) / 60000)
      const base = rising ? "Scores rose on each try" : "Scores rose across the tries"
      const spread = spanMin > 0 && spanMin < 240 ? `, spread over ${spanMin} minutes` : ""
      return `${base}${spread}.${nextTitle ? ` Ready for ${nextTitle}, the recommended next step.` : ""}`
    }
    case "Slow gain":
      return "Small gain on the retry. Worth a short teach before the next attempt."
    case "Guessing":
      return "Quick retries with almost no gain. Looks like guessing, so slow the next attempt down."
    case "Stalled":
      return "Little change across the tries. Re-teach the skill before another attempt."
    default:
      return ""
  }
}

export function groupActivities(
  attempts: LearnerAttempt[],
  nextTitleBySkill: (subject: AiSubject, skill: string) => string | null
): ActivityGroup[] {
  const map = new Map<string, LearnerAttempt[]>()
  attempts.forEach(a => {
    const key = `${a.subject}|${a.source_id}`
    const list = map.get(key)
    if (list) list.push(a)
    else map.set(key, [a])
  })
  const groups: ActivityGroup[] = []
  map.forEach((list, key) => {
    const sorted = list
      .map(a => ({ a, at: new Date(a.attempted_on) }))
      .filter(x => !Number.isNaN(x.at.getTime()))
      .sort((x, y) => x.at.getTime() - y.at.getTime())
    if (sorted.length === 0) return
    const head = sorted[sorted.length - 1].a
    const tries = sorted.map(x => ({ at: x.at, score: x.a.score }))
    const scores = tries.map(t => t.score).filter((s): s is number => s !== null)
    const { gain, verdict } = verdictFor(tries)
    const base = {
      key,
      subject: head.subject,
      sourceId: head.source_id,
      title: head.title,
      typeLabel: head.type_label,
      skill: head.skill,
      strand: head.strand,
      year: head.year,
      tries,
      best: scores.length ? Math.max(...scores) : null,
      last: tries[tries.length - 1].at,
      first: tries[0].at,
      gainPerTry: gain,
      verdict,
    }
    groups.push({ ...base, note: noteFor(base, nextTitleBySkill(head.subject, head.skill)) })
  })
  return groups.sort((x, y) => y.last.getTime() - x.last.getTime())
}

// ---------------------------------------------------------------------------
// Recommendations → learning path
// ---------------------------------------------------------------------------

export type PathWhen = "NOW" | "NEXT" | "THEN" | "REVISIT"

export interface PathItem {
  when: PathWhen
  basket: "Progress" | "Relearn" | "Preference"
  title: string
  sourceId: string
  strand: string
  skill: string
  level: number | null
  reason: string
  meta: string
}

export interface SubjectRecs {
  subject: AiSubject
  data: RecommendationApiData | null
  progress: PathItem[]
  relearn: PathItem[]
  preference: PathItem[]
  total: number
}

function toPathItem(
  item: RecommendationApiBasketItem,
  basket: PathItem["basket"],
  when: PathWhen,
  placement: { skill: string; strand: string; year: number | null } | undefined
): PathItem {
  const levels = Array.isArray(item.levels) ? item.levels.filter(n => typeof n === "number") : []
  const level = levels.length ? levels[0] : typeof item.year === "number" ? item.year : placement?.year ?? null
  const reason = Array.isArray(item.reasons) && item.reasons.length ? item.reasons[0] : ""
  const strand = item.strand || placement?.skill || ""
  const meta = [strand, level !== null ? `Y${level}` : "", reason, item.sourceId]
    .filter(Boolean)
    .join(" · ")
  return {
    when,
    basket,
    title: item.title,
    sourceId: item.sourceId,
    strand,
    skill: placement?.skill || item.strand || "",
    level,
    reason,
    meta,
  }
}

const PATH_WHEN: PathWhen[] = ["NOW", "NEXT", "THEN"]

export function buildSubjectRecs(
  subject: AiSubject,
  data: RecommendationApiData | null,
  report: LearnerReportData | null
): SubjectRecs {
  const placements = new Map(
    (report?.recommended || []).map(r => [r.source_id, r])
  )
  const empty = { subject, data, progress: [], relearn: [], preference: [], total: 0 }
  if (!data) return empty
  const sections = basketSections(data)
  const find = (key: string) =>
    sections.find(s => s.reason === key)?.items.filter(i => i?.sourceId && i?.title) || []
  const progress = find("progress").map((item, idx) =>
    toPathItem(item, "Progress", PATH_WHEN[idx] || "THEN", placements.get(item.sourceId))
  )
  const relearn = find("relearn").map(item =>
    toPathItem(item, "Relearn", "REVISIT", placements.get(item.sourceId))
  )
  const preference = find("preference").map(item =>
    toPathItem(item, "Preference", "THEN", placements.get(item.sourceId))
  )
  const total =
    typeof data.totalCount === "number"
      ? data.totalCount
      : progress.length + relearn.length + preference.length
  return { subject, data, progress, relearn, preference, total }
}

// ---------------------------------------------------------------------------
// Skills
// ---------------------------------------------------------------------------

export interface SkillYear {
  year: number
  score: number | null
}

export interface SkillActivity {
  name: string
  meta: string
  score: string
}

export interface SkillRow {
  name: string
  strand: string
  range: string
  score: number | null
  band: BandStyle
  years: SkillYear[]
  heat: SkillYear[]
  slip: string
  acts: SkillActivity[]
  next: PathItem | null
}

export interface StrandGroup {
  name: string
  color: string
  skills: SkillRow[]
}

export interface SubjectSkills {
  subject: AiSubject
  currentYear: number | null
  heatYears: number[]
  strands: StrandGroup[]
  skills: SkillRow[]
  counts: Record<SkillBand, number>
  passed: number
  total: number
  avg: number | null
  close: number
  lockLabel: string
}

function slipLabel(cells: SkillYear[]): string {
  let seen = false
  for (const c of cells) {
    if (c.score === null) continue
    if (c.score >= 7) {
      seen = true
      continue
    }
    return seen ? `Y${c.year}` : `From Y${c.year}`
  }
  return seen ? "Secure" : "Not attempted"
}

export function buildSubjectSkills(
  subject: AiSubject,
  report: LearnerReportData,
  groups: ActivityGroup[],
  recs: SubjectRecs,
  fallbackYear: number | null
): SubjectSkills {
  const currentYear = report.student.current_year ?? fallbackYear
  const curriculum = report.curriculum.filter(c => c.subject === subject)
  const subjectGroups = groups.filter(g => g.subject === subject)

  const names = new Map<string, { name: string; strand: string }>()
  const add = (name: string, strand: string) => {
    if (!name) return
    const k = keyOf(name)
    if (!names.has(k)) names.set(k, { name, strand: strand || name })
  }
  if (currentYear !== null) {
    curriculum.filter(c => c.year === currentYear).forEach(c => add(c.skill, c.strand))
    subjectGroups.filter(g => g.year === currentYear).forEach(g => add(g.skill, g.strand))
  }
  if (names.size === 0) subjectGroups.forEach(g => add(g.skill, g.strand))

  const heatYears: number[] = []
  if (currentYear !== null) {
    for (let y = Math.max(1, currentYear - 3); y <= currentYear; y++) heatYears.push(y)
  }

  const scoreAt = (skillKey: string, year: number | null): number | null => {
    const bests = subjectGroups
      .filter(g => keyOf(g.skill) === skillKey && (year === null || g.year === year))
      .map(g => g.best)
      .filter((s): s is number => s !== null)
    const a = avg(bests)
    return a === null ? null : Math.round(a) / 10
  }

  const recItems = [...recs.progress, ...recs.relearn]
  const skills: SkillRow[] = Array.from(names.entries()).map(([k, { name, strand }]) => {
    const curYears = curriculum
      .filter(c => keyOf(c.skill) === k)
      .map(c => c.year)
    const lo = curYears.length ? Math.min(...curYears) : currentYear
    const range =
      lo !== null && currentYear !== null
        ? lo === currentYear
          ? `Y${currentYear}`
          : `Y${lo}–Y${currentYear}`
        : "All years"
    const years: SkillYear[] =
      currentYear !== null && lo !== null
        ? Array.from({ length: currentYear - lo + 1 }, (_, i) => ({
            year: lo + i,
            score: scoreAt(k, lo + i),
          }))
        : []
    const score = currentYear !== null ? scoreAt(k, currentYear) : scoreAt(k, null)
    const heat = heatYears.map(y => ({ year: y, score: scoreAt(k, y) }))
    const acts = subjectGroups
      .filter(g => keyOf(g.skill) === k && (currentYear === null || g.year === currentYear))
      .map(g => ({
        name: g.title,
        meta: `${g.tries.length} ${g.tries.length === 1 ? "try" : "tries"}`,
        score: g.best === null ? "–" : g.best.toFixed(1),
      }))
    const next =
      recItems.find(r => keyOf(r.skill) === k) ||
      recItems.find(r => keyOf(r.strand) === k) ||
      null
    return {
      name,
      strand,
      range: currentYear !== null ? `${range} · current Y${currentYear}` : range,
      score,
      band: skillBand(score),
      years: years.length ? years : [{ year: currentYear ?? 0, score }],
      heat,
      slip: slipLabel(heat),
      acts,
      next,
    }
  })

  const strandMap = new Map<string, SkillRow[]>()
  skills.forEach(s => {
    const list = strandMap.get(s.strand)
    if (list) list.push(s)
    else strandMap.set(s.strand, [s])
  })
  const strands: StrandGroup[] = Array.from(strandMap.entries()).map(([name, list], i) => ({
    name,
    color: STRAND_COLORS[i % STRAND_COLORS.length],
    skills: list,
  }))

  const counts: Record<SkillBand, number> = {
    "Not started": 0, Passed: 0, Good: 0, "In progress": 0, Weak: 0,
  }
  skills.forEach(s => {
    counts[s.band.label] += 1
  })
  const scored = skills.map(s => s.score).filter((s): s is number => s !== null)
  const total = skills.length
  const allPassed = total > 0 && counts.Passed === total
  const lockLabel =
    currentYear === null
      ? ""
      : allPassed
      ? `Year ${currentYear + 1} unlocked · current year passed`
      : `Year ${currentYear + 1} locked · current year not passed`

  return {
    subject,
    currentYear,
    heatYears,
    strands,
    skills,
    counts,
    passed: counts.Passed,
    total,
    avg: avg(scored),
    close: counts.Good,
    lockLabel,
  }
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export interface TrendRow {
  name: string
  subject: AiSubject
  values: number[]
  first: number
  cur: number
  diff: number
  dir: "up" | "down" | "flat"
}

export function buildTrends(groups: ActivityGroup[], now: Date): TrendRow[] {
  const weekEnds = Array.from({ length: 8 }, (_, i) => now.getTime() - (7 - i) * 7 * DAY_MS)
  const bySkill = new Map<string, ActivityGroup[]>()
  groups.forEach(g => {
    if (!g.skill) return
    const k = `${g.subject}|${keyOf(g.skill)}`
    const list = bySkill.get(k)
    if (list) list.push(g)
    else bySkill.set(k, [g])
  })
  const rows: TrendRow[] = []
  bySkill.forEach(list => {
    const tries = list.flatMap(g =>
      g.tries.filter(t => t.score !== null).map(t => ({ g: g.key, at: t.at.getTime(), score: t.score as number }))
    )
    if (tries.length === 0) return
    const values: Array<number | null> = weekEnds.map(end => {
      const best = new Map<string, number>()
      tries.forEach(t => {
        if (t.at > end) return
        best.set(t.g, Math.max(best.get(t.g) ?? 0, t.score))
      })
      const a = avg(Array.from(best.values()))
      return a === null ? null : Math.round(a) / 10
    })
    const firstKnown = values.find((v): v is number => v !== null)
    if (firstKnown === undefined) return
    const filled = values.map(v => (v === null ? firstKnown : v))
    const first = filled[0]
    const cur = filled[filled.length - 1]
    const diff = cur - first
    rows.push({
      name: list[0].skill,
      subject: list[0].subject,
      values: filled,
      first,
      cur,
      diff,
      dir: Math.abs(diff) < 0.3 ? "flat" : diff > 0 ? "up" : "down",
    })
  })
  return rows
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
    .slice(0, 12)
}

export const STUDY_SLOTS = ["Before school", "Daytime", "After school", "Evening"] as const

function slotOf(d: Date): number {
  const h = d.getHours()
  if (h < 8) return 0
  if (h < 15) return 1
  if (h < 18) return 2
  return 3
}

function dayIndex(d: Date): number {
  return (d.getDay() + 6) % 7
}

export interface AttemptBehaviour {
  attempts: number
  activities: number
  perActivity: string
  rapid: number
  gain: string
  mostActiveDay: string
  mostActiveSlot: string
  ladders: ActivityGroup[]
  grid: number[][]
  summary: string
}

export function buildBehaviour(groups: ActivityGroup[]): AttemptBehaviour {
  const attempts = groups.reduce((t, g) => t + g.tries.length, 0)
  const multi = groups.filter(g => g.tries.length > 1 && g.gainPerTry !== null)
  const gains = multi.map(g => g.gainPerTry as number)
  const gainAvg = avg(gains)

  const grid = STUDY_SLOTS.map(() => DAYS.map(() => 0))
  const seen = new Set<string>()
  const dayTotals = DAYS.map(() => 0)
  const slotTotals = STUDY_SLOTS.map(() => 0)
  groups.forEach(g =>
    g.tries.forEach(t => {
      const s = slotOf(t.at)
      const d = dayIndex(t.at)
      const k = `${t.at.toDateString()}|${s}`
      if (seen.has(k)) return
      seen.add(k)
      grid[s][d] += 1
      dayTotals[d] += 1
      slotTotals[s] += 1
    })
  )
  const topDay = dayTotals.indexOf(Math.max(...dayTotals))
  const topSlot = slotTotals.indexOf(Math.max(...slotTotals))
  const totalSessions = dayTotals.reduce((t, n) => t + n, 0)
  const weekend = dayTotals[5] + dayTotals[6]

  let summary = "No practice sessions recorded in this period."
  if (totalSessions > 0) {
    summary = `Practice clusters ${STUDY_SLOTS[topSlot].toLowerCase()}, most often on ${DAYS[topDay]}.`
    summary +=
      weekend === 0
        ? " No weekend sessions."
        : weekend <= Math.max(1, Math.round(totalSessions * 0.2))
        ? " Weekend sessions are short and rare."
        : " Weekends are used regularly too."
  }

  return {
    attempts,
    activities: groups.length,
    perActivity: groups.length ? (attempts / groups.length).toFixed(1) : "–",
    rapid: groups.filter(g => g.verdict === "Guessing").length,
    gain: gainAvg === null ? "–" : `${gainAvg >= 0 ? "+" : "−"}${Math.abs(gainAvg).toFixed(1)}`,
    mostActiveDay: totalSessions ? DAYS[topDay] : "–",
    mostActiveSlot: totalSessions ? STUDY_SLOTS[topSlot] : "No sessions yet",
    ladders: multi.sort((a, b) => b.tries.length - a.tries.length).slice(0, 3),
    grid,
    summary,
  }
}

export const WEEK_DAYS = DAYS

export function sinceDays(now: Date, days: number): Date {
  return new Date(now.getTime() - days * DAY_MS)
}

export function academicYearStart(now: Date, country: string | null | undefined): Date {
  const uk = !country || /^(gb|uk)$/i.test(country)
  if (!uk) return new Date(now.getFullYear(), 0, 1)
  const year = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1
  return new Date(year, 8, 1)
}
